import { Permission } from "../models";
import { PermissionState } from "../contexts/types";
import { PermissionCode, PermissionLevel } from "../types";

/**
 * Converts an array of `Permission` objects into a structured `PermissionState` dictionary.
 *
 * This function maps permission codes to their corresponding permission levels
 * and organizes them into a nested dictionary structure. Each permission code
 * is divided into a key (first 4 characters) and a numeric level (last 3 characters),
 * which is then translated into a human-readable permission level.
 *
 * @param permissions - An array of `Permission` objects, each containing a `code` property.
 * @returns A `PermissionState` object where permissions are grouped by their keys
 *          and levels, with boolean values indicating the presence of each level.
 *
 * @example
 * ```typescript
 * const permissions = [
 *   { code: "ALLP-000" },
 *   { code: "REPO-001" },
 *   { code: "WORK-002" },
 * ];
 *
 * const result = permissionsToDict(permissions);

 * // {
 * //   permissions: {
 * //     ALLP: { ALL: true },
 * //     REPO: { VIEW: true },
 * //     WORK: { CREATE: true },
 * //     ...
 * //   }
 * // }
 * ```
 */
export function permissionsToDict(permissions: Permission[]): PermissionState {
  const numToLevel: Record<string, PermissionLevel> = {
    "000": "ALL",
    "001": "VIEW",
    "002": "CREATE",
    "003": "EDIT",
    "004": "DELETE",
    "005": "TEST",
    "006": "MISC",
  };

  const codeDict: PermissionState = {
    permissions: {
      ALLP: {},
      REPO: {},
      WORK: {},
      FILE: {},
      SETT: {},
      ROLE: {},
      USER: {},
      VIEW: {},
      LOGS: {},
      CONN: {},
      PPGS: {},
      EXPO: {},
      EXPL: {},
      DASH: {},
      APPS: {},
    },
  };

  for (const { code } of permissions) {
    const key = code.slice(0, 4);
    const num = code.slice(-3);
    const level = numToLevel[num];

    if (!level) continue;

    if (!codeDict.permissions[key]) {
      codeDict.permissions[key] = {};
    }

    codeDict.permissions[key][level] = true;
  }

  return codeDict;
}

/**
 * Helper function to check if a role has a permission
 * @param permissionState Current role's permissions
 * @param code Permission code to check
 * @param level Permission level to check
 * @returns boolean indicating whether the role has the permission
 */
export function checkForPermissionLevel(
  permissionState: PermissionState,
  code: PermissionCode,
  level: PermissionLevel,
): boolean {
  const { permissions } = permissionState;
  return level in permissions[code];
}
